import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router";
import { queryClient, trpc } from "../../app/trpc.js";
import { Button, Card, Input, Label, Notice, Spinner } from "../../ui/index.js";
import { AdminTopBar } from "./AdminTopBar.js";
import { alertLabel, fmtDate, fmtInt, fmtUsd, salonStatusLabel, subscriptionLabel } from "./format.js";

/** Наша админка: все салоны, подключение Instagram, подписка, расход, тревоги; создание салона для клиента. */
export function AdminSalonsPage() {
  const salons = useQuery(trpc.billing.admin.salons.queryOptions());
  const orphans = useQuery(trpc.billing.admin.orphanAlerts.queryOptions());
  const resolve = useMutation(trpc.billing.admin.resolveAlert.mutationOptions({ onSuccess: () => void orphans.refetch() }));
  const [creating, setCreating] = useState(false);
  // Строка с «Выйти» есть и пока салоны грузятся, и если не загрузились.
  const shell = (children: ReactNode) => (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <AdminTopBar className="mb-6" left={<span className="font-semibold text-ink">Админка</span>} />
      {children}
    </div>
  );
  if (salons.isPending) return shell(<Spinner />);
  if (salons.isError) return shell(<Notice kind="error">Не удалось загрузить салоны</Notice>);
  const rows = salons.data;
  return shell(
    <>
      {orphans.data && orphans.data.length > 0 && (
        <div className="mb-6 rounded-card border border-red-200 bg-red-50 p-4 text-sm">
          <h2 className="mb-2 font-semibold text-red-800">Тревоги без салона</h2>
          <ul className="space-y-1">
            {orphans.data.map((a) => (
              <li key={a.id}>
                {fmtDate(a.createdAt)} · {alertLabel[a.kind] ?? a.kind} · {a.message}
                <button type="button" className="ml-2 underline" onClick={() => resolve.mutate({ alertId: a.id })}>
                  закрыть
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-ink lg:text-3xl">Салоны ({rows.length})</h1>
        {!creating && <Button onClick={() => setCreating(true)}>Создать салон</Button>}
      </div>
      {creating && <CreateSalonForm onCancel={() => setCreating(false)} />}
      {rows.length === 0 ? (
        <Notice>Салонов пока нет.</Notice>
      ) : (
        <div className="overflow-x-auto rounded-card border border-line bg-white shadow-card">
          <table className="w-full text-left text-sm">
            <thead className="bg-page text-xs uppercase tracking-wider text-ink-3">
              <tr className="border-b border-line">
                <th className="px-4 py-2.5 font-semibold">Салон</th>
                <th className="px-4 py-2.5 font-semibold">Владелец</th>
                <th className="px-4 py-2.5 font-semibold">Instagram</th>
                <th className="px-4 py-2.5 font-semibold">Подписка</th>
                <th className="px-4 py-2.5 font-semibold">Сегодня</th>
                <th className="px-4 py-2.5 font-semibold">30 дней</th>
                <th className="px-4 py-2.5 font-semibold">Тревоги</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="border-b border-line align-top last:border-b-0 hover:bg-slate-50">
                  <td className="px-4 py-2.5">
                    <Link to={`/admin/salons/${s.id}`} className="font-medium text-brand underline-offset-2 hover:underline">
                      {s.name}
                    </Link>
                    <div className={`text-xs ${s.status === "deleted" ? "text-red-600" : "text-ink-3"}`}>{salonStatusLabel[s.status] ?? s.status}</div>
                  </td>
                  <td className="px-4 py-2.5">{s.ownerEmail ?? <span className="text-ink-3">нет</span>}</td>
                  <td className="px-4 py-2.5">
                    {s.instagram ? (
                      <>
                        <div className={s.instagram.status === "connected" ? "text-green-700" : "text-red-600"}>
                          {s.instagram.username ? `@${s.instagram.username}` : "без имени"} · {s.instagram.status}
                        </div>
                        <div className="text-xs text-ink-3">вебхук: {fmtDate(s.instagram.lastWebhookAt)}</div>
                      </>
                    ) : (
                      <span className="text-ink-3">не подключён</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {subscriptionLabel[s.subscription.status] ?? s.subscription.status}
                    {s.subscription.cancelAt && <div className="text-xs text-amber-700">отмена {fmtDate(s.subscription.cancelAt)}</div>}
                  </td>
                  <td className="px-4 py-2.5">{fmtUsd(s.usage.todayUsd)}</td>
                  <td className="px-4 py-2.5">
                    {fmtUsd(s.usage.last30Usd)}
                    <div className="text-xs text-ink-3">
                      {fmtInt(s.usage.last30Calls)} вызовов · {fmtInt(s.usage.last30Tokens)} токенов
                    </div>
                  </td>
                  <td className={`px-4 py-2.5 ${s.openAlerts > 0 ? "font-medium text-red-600" : "text-ink-3"}`}>{s.openAlerts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>,
  );
}

/**
 * Салон для клиента (пакет «подключим за вас»): админ не становится владельцем, владельцу уходит письмо
 * с приглашением (роль owner). После создания: карточка салона.
 */
function CreateSalonForm({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const create = useMutation(
    trpc.auth.admin.createSalonForClient.mutationOptions({
      onSuccess: async (r) => {
        await queryClient.invalidateQueries(trpc.billing.admin.salons.pathFilter());
        navigate(`/admin/salons/${r.salonId}`, { state: r.mailed ? null : { mailFailed: true } });
      },
    }),
  );
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate({ name: name.trim(), ownerEmail: email.trim() });
  };
  return (
    <Card className="mb-6">
      <h2 className="mb-1 font-semibold">Новый салон для клиента</h2>
      <p className="mb-3 text-sm text-ink-2">
        Владельцу уйдёт письмо с приглашением (ссылка на 7 дней). Вы владельцем не станете: вход в салон пишется в журнал.
      </p>
      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-3">
        <div>
          <Label htmlFor="new-salon-name">Название</Label>
          <Input id="new-salon-name" value={name} onChange={(e) => setName(e.target.value)} className="w-64" required minLength={2} maxLength={100} />
        </div>
        <div>
          <Label htmlFor="new-salon-owner">Почта владельца</Label>
          <Input id="new-salon-owner" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-64" required />
        </div>
        <Button type="submit" disabled={create.isPending || name.trim().length < 2 || !email.trim()}>
          Создать
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={create.isPending}>
          Отмена
        </Button>
      </form>
      {create.isError && (
        <div className="mt-3">
          <Notice kind="error">Не удалось создать салон: {create.error.message}</Notice>
        </div>
      )}
    </Card>
  );
}
